/**
 * Password change modal component for user settings
 */
import React, { useEffect, useState } from "react";
import { Info } from "lucide-react";
import api from "config/axiosConfig";
import Modal from "ui/Modal";
import InputField from "ui/InputField";

interface UserSettingsAccountPasswordProps {
    isOpen: boolean;
    onClose: () => void;
}

const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    maxLength: 100
};

const UserSettingsAccountPassword: React.FC<UserSettingsAccountPasswordProps> = ({
    isOpen,
    onClose
}) => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [validationMessage, setValidationMessage] = useState<string>('')

    const validatePassword = (password: string): string | null => {
        setValidationMessage('');

        if (password.length < PASSWORD_REQUIREMENTS.minLength) {
            setValidationMessage("Password must be at least 8 characters long");
        }
        if (password.length > PASSWORD_REQUIREMENTS.maxLength) {
            setValidationMessage("Password must not exceed 100 characters");
        }

        if (currentPassword === newPassword) {
            setValidationMessage("New password must be different from current password");
        }
        return null;


    };

    const handlePasswordChange = async () => {
        setError(null);
        setIsSubmitting(true);

        // Validate new password
        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setError(passwordError);
            setIsSubmitting(false);
            return;
        }

        // Validate confirmation matches
        if (newPassword !== confirmPassword) {
            setError("New passwords do not match");
            setIsSubmitting(false);
            return;
        }

        // Validate new password is different from current
        if (newPassword === currentPassword) {
            setError("New password must be different from current password");
            setIsSubmitting(false);
            return;
        }

        try {
            await api.post("/auth/password/change", {
                currentPassword,
                newPassword
            });

            onClose();
            // Reset form
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setShowConfirmation(false);
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to change password");
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (newPassword && newPassword.length > 1) {
            setValidationMessage('');
    
            if (newPassword.length < PASSWORD_REQUIREMENTS.minLength) {
                setValidationMessage("Password must be at least 8 characters long");
            }

            if (newPassword.length > PASSWORD_REQUIREMENTS.maxLength) {
                setValidationMessage("Password must not exceed 100 characters");
            }
    
            if (currentPassword === newPassword) {
                setValidationMessage("New password must be different from current password");
            }
        } else {
            setValidationMessage('');
        }
    }, [newPassword, currentPassword]);



    const handleSubmit = () => {
        if (!currentPassword || !newPassword || !confirmPassword) {

            setError("All fields are required");
            return;
        }
        setShowConfirmation(true);
    };

    const isButtonDisabled = !currentPassword || 
                           !newPassword || 
                           !confirmPassword || 
                           isSubmitting || 
                           newPassword.length < PASSWORD_REQUIREMENTS.minLength;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Change password"
            confirmationText="Are you sure you want to change your password?"
            onConfirm={handlePasswordChange}
            showConfirmation={showConfirmation}
            onCancelConfirmation={() => setShowConfirmation(false)}
        >
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Please enter your current password and choose a new password.
                    </p>

                    <form className="flex flex-col flex-grow space-y-4">
                        <div className="form-control mb-0">
                            <InputField
                                type="password"
                                id="currentPassword"
                                name="currentPassword"
                                labelText="Current password"
                                autoComplete="current-password"
                                fieldRequired
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                spellCheck={false}
                            />
                        </div>

                        <div className="form-control">
                            <InputField
                                type="password"
                                id="newPassword"
                                name="newPassword"
                                labelText="New password"
                                autoComplete="new-password"
                                fieldRequired
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                validationState={newPassword.length >= PASSWORD_REQUIREMENTS.minLength ? 'valid' : newPassword ? 'invalid' : 'none'}
                                error={validationMessage}
                                spellCheck={false}
                                maxLength={PASSWORD_REQUIREMENTS.maxLength}
                                minLength={PASSWORD_REQUIREMENTS.minLength}
                            />
                        </div>

                        <div className="form-control">
                            <InputField
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                labelText="Confirm new password"
                                autoComplete="new-password"
                                fieldRequired
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                validationState={confirmPassword && confirmPassword === newPassword ? 'valid' : confirmPassword ? 'invalid' : 'none'}
                                error={validationMessage}
                                spellCheck={false}
                                maxLength={PASSWORD_REQUIREMENTS.maxLength}
                                minLength={PASSWORD_REQUIREMENTS.minLength}
                            />
                        </div>

                        {error && (
                            <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                <Info className="mr-1 size-4" />
                                <span>{error}</span>
                            </div>
                        )}
                    </form>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isButtonDisabled}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default UserSettingsAccountPassword; 